import React, { useMemo, useState } from 'react';
import { Flex, Text } from 'onefaceui-react-components';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  getDeliveryComplianceByStatusClass,
  getTWComplianceByStatusClass,
  getResourceTo
} from 'react/utils/utils';

import { outsideTimeWindowIcon } from 'react/utils/stopDetailsPopupBase64Icons';
import OrderAndItemsModal from 'react/containers/modals/OrderAndItemsModal';
import { STOP_STATUS, formatNextStopMessageTooltip } from 'react/utils/stopUtils';
import Tooltip from 'react/components/Tooltip';
import { getAddressByStopLocation, getSizeAliasListByStop } from './utils';
import StopActionsMenu from './StopActionsMenu';

import { Stop } from './styles';

const renderPendingStopStatusIcon = (plannedSequenceNum) => (
  <Text className="content pending">
    {plannedSequenceNum}
  </Text>
);

const renderPendingStopStatusIconWithNextStop = (
  plannedSequenceNum
) => (
  <>
    <Flex className="iconNextStop" />
    {renderPendingStopStatusIcon(plannedSequenceNum)}
  </>
);

const decidePendingStopStatusIcon = (confirmedAsNextStop, projectedArrival, plannedSequenceNum) => {
  if (confirmedAsNextStop && projectedArrival) {
    return renderPendingStopStatusIconWithNextStop(plannedSequenceNum);
  }
  return renderPendingStopStatusIcon(plannedSequenceNum);
};

const getActualPlannedLabel = (actualSequenceNum) => (actualSequenceNum
  ? getResourceTo('modal.stopdetails.actualplanned')
  : getResourceTo('modal.stopdetails.planned'));

const getSequenceLabel = (actualSequenceNum) => {
  const actualPlannedLabel = getActualPlannedLabel(actualSequenceNum);
  return `${getResourceTo('greenmile.routesuggestion.grid.plannedseq')}: ${actualPlannedLabel}`;
};

const formatTimeWindow = (timeToFormat) => moment(timeToFormat, 'HH:mm').format('hh:mmA');

const pushLocationPhones = ({ phone1, phone2 }) => {
  const phones = [];
  if (phone1 && phone1 !== 0) {
    phones.push(phone1);
  }
  if (phone2 && phone2 !== 0) {
    phones.push(phone2);
  }
  return phones;
};

/**
 * StopListItem Component - Displays a single stop in the route monitor
 *
 * @param {Object} props - Component props
 * @param {Object} props.stop - Stop data object
 * @param {number} props.routeId - ID of the route
 * @param {string} props.stopDescription - Description text for the stop
 * @returns {JSX.Element} Rendered component
 */
function StopListItem({ stop, routeId, stopDescription }) {
  const [orderAndItemIsOpen, setOrderAndItemIsOpen] = useState(false);
  const {
    statusClass,
    deliveryStatus,
    actualSequenceNum,
    plannedSequenceNum,
    projectedArrival,
    actualArrival,
    location,
    confirmedAsNextStop,
    id
  } = stop;
  const phones = pushLocationPhones(location || {});

  const address = getAddressByStopLocation(location);
  const sizeAliasConfigs = getSizeAliasListByStop(stop);

  const deliveryCompliance = getDeliveryComplianceByStatusClass(statusClass);
  const twCompliance = getTWComplianceByStatusClass(statusClass);

  const formatTimeData = useMemo(() => {
    const date = moment(actualArrival || projectedArrival);
    return {
      time: date.format('hh:mm'),
      period: date.format('A')
    };
  }, [actualArrival, projectedArrival]);

  const { time } = formatTimeData;
  const { period } = formatTimeData;

  const addTimeWindowIfValid = (start, end) => (start && end
    ? `${formatTimeWindow(start)} - ${formatTimeWindow(end)}`
    : null);

  const getFormattedTimeWindows = useMemo(() => {
    const {
      plannedTimeWindowOpen,
      plannedTimeWindowClose,
      baselineTimeWindowOpen,
      baselineTimeWindowClose
    } = stop;

    const timeWindows = [
      addTimeWindowIfValid(plannedTimeWindowOpen, plannedTimeWindowClose),
      addTimeWindowIfValid(baselineTimeWindowOpen, baselineTimeWindowClose)
    ].filter(Boolean);

    return timeWindows.length ? timeWindows.join(' / ') : '';
  }, [location]);

  const toggleOrderAndItemsModal = () => {
    setOrderAndItemIsOpen((prevState) => !prevState);
  };

  const allowArriveItem = useMemo(
    () => stop.route
      && stop.route.status === 'DEPARTED_ORIGIN'
      && !stop.cancelCode
      && !stop.actualArrival,
    [stop]
  );

  const allowDepartStop = useMemo(
    () => stop.route
      && stop.route.status === 'DEPARTED_ORIGIN'
      && stop.actualArrival
      && !stop.actualDeparture
      && !stop.cancelCode,
    [stop]
  );

  const allowUndeliveryStop = useMemo(
    () => allowDepartStop
      && stop.stopType
      && stop.stopType.type === 'STOP'
      && !!location,
    [allowDepartStop, stop, location]
  );

  const allowSeeOrdersAndItems = !!location;

  const shouldShowTimeWindowAndCompliance = useMemo(() => [
    STOP_STATUS.DELIVERED, STOP_STATUS.PARTIALLY_DELIVERED
  ].includes(
    deliveryStatus
  ), [deliveryStatus]);

  const shouldShowTimeWindow = useMemo(() => shouldShowTimeWindowAndCompliance
    && !!getFormattedTimeWindows, [shouldShowTimeWindowAndCompliance, getFormattedTimeWindows]);

  const isTheNextStop = confirmedAsNextStop && projectedArrival;

  return (
    <Stop data-testid={`stop-item-${id}`} key={id}>
      <Flex
        className={`status ${
          deliveryStatus ? deliveryStatus.toLowerCase() : ''
        }`}
        justifyContent="center"
        alignItems="center"
      />

      <Flex
        data-testid={`stop-status-${id}`}
        className={`stop-status ${
          deliveryStatus ? deliveryStatus.toLowerCase() : ''
        }`}
        flexDirection="column"
      >
        <Tooltip
          title={
            isTheNextStop
              ? formatNextStopMessageTooltip(projectedArrival)
              : getResourceTo(stopDescription)
          }
          styles={isTheNextStop ? { tooltipBubble: { with: '200px', whiteSpace: 'normal' } } : null}
          position="bottom-right"
        >
          <Flex
            className={`icon-content ${
              deliveryStatus ? deliveryStatus.toLowerCase() : ''
            }`}
          >
            {deliveryStatus === 'PENDING' ? (
              decidePendingStopStatusIcon(
                confirmedAsNextStop,
                projectedArrival,
                plannedSequenceNum
              )
            ) : (
              <Flex
                className={`content ${
                  deliveryStatus ? deliveryStatus.toLowerCase() : ''
                }`}
              />
            )}
          </Flex>
        </Tooltip>
        <Text className="stop-status-date">{time}</Text>
        <Text className="stop-status-date">{period}</Text>
        {deliveryStatus !== 'PENDING' && deliveryStatus !== 'CANCELED' && (
          <Tooltip
            title={getSequenceLabel(actualSequenceNum)}
            position="bottom-right"
          >
            <Flex justifyContent="center" weight="bold">
              {actualSequenceNum && (
                <Text className="stop-status-sequence">{`${actualSequenceNum}/`}</Text>
              )}
              <Text className="stop-status-sequence">{plannedSequenceNum}</Text>
            </Flex>
          </Tooltip>
        )}
      </Flex>

      <Flex flexDirection="column" className="stop-content">
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Flex flexDirection="column" gap={4}>
            <Text size="sm" weight="bold" data-testid={`item-${id}`}>
              {location
                ? [location.key, location.description]
                  .filter(Boolean)
                  .join(' - ')
                : getResourceTo('modal.stopdetails.view.nokeyanddescription')}
            </Text>

            {shouldShowTimeWindowAndCompliance && (
              <>
                <Flex
                  shouldShow={shouldShowTimeWindow}
                  alignItems="center"
                  gap={4}
                  data-testid={`item-${twCompliance.twStatus}`}
                >
                  {twCompliance.twStatus === 'outside' && (
                    <img
                      src={outsideTimeWindowIcon}
                      alt="Time Window Icon"
                      className="icon-outside-time-window"
                    />
                  )}
                  <Text
                    size="xs"
                    data-testid={`stop-item-${id}-status`}
                    className={`twStatus status-${twCompliance.twStatus}`}
                  >
                    {getFormattedTimeWindows}
                  </Text>
                </Flex>
                <Flex gap={12}>
                  <Flex
                    shouldShow={shouldShowTimeWindow}
                    alignItems="center"
                    gap={2}
                  >
                    <img
                      src={twCompliance.icon}
                      alt="Delivery Icon"
                      className="icon-delivery"
                    />
                    <Text size="xxs" asChild>
                      <span>{getResourceTo(twCompliance.resource)}</span>
                    </Text>
                  </Flex>

                  {/* Delivery compliance */}
                  <Flex alignItems="center" gap={2}>
                    <img
                      src={deliveryCompliance.icon}
                      alt={`Stop status Icon - ${statusClass}`}
                      className="icon-delivery"
                    />
                    <Text size="xxs" asChild>
                      <span>{getResourceTo(deliveryCompliance.resource)}</span>
                    </Text>
                  </Flex>
                </Flex>
              </>
            )}

            <Text
              data-testid={`stop-item-${id}-address`}
              className="address-text"
              size="xs"
            >
              {address || getResourceTo('modal.stopdetails.view.noaddress')}
            </Text>

            <Flex
              shouldShow={!!phones.length}
              data-testid={`stop-item-${id}-phone`}
              alignItems="center"
              gap={22}
            >
              {phones.map((phone, index) => (
                <Flex alignItems="center" gap={4} key={phone}>
                  <div className={`phone-icon phone${index + 1}`} />
                  <Text size="xs" weight="medium">
                    {phone}
                  </Text>
                </Flex>
              ))}
            </Flex>

            <Flex
              shouldShow={!!sizeAliasConfigs.length}
              alignItems="center"
              gap={4}
            >
              {sizeAliasConfigs.map(
                ({
                  id: sizeId, icon, description, sizeInfo
                }) => (
                  <Flex
                    key={sizeId}
                    alignItems="center"
                    gap={4}
                    className="size-item"
                  >
                    <i className={icon} />
                    <Tooltip
                      title={`${description}:${sizeInfo.title}`}
                      position="top"
                    >
                      <Text dataTestId={`size-item-${id}`} size="xs">
                        {sizeInfo.size}
                      </Text>
                    </Tooltip>
                  </Flex>
                )
              )}
            </Flex>
          </Flex>

          <StopActionsMenu
            stop={stop}
            routeId={routeId}
            toggleOrderAndItemsModal={toggleOrderAndItemsModal}
            allowArriveItem={allowArriveItem}
            allowDepartStop={allowDepartStop}
            allowUndeliveryStop={allowUndeliveryStop}
            allowSeeOrdersAndItems={allowSeeOrdersAndItems}
          />
        </Flex>
      </Flex>
      {orderAndItemIsOpen && (
        <OrderAndItemsModal
          stopId={id}
          isOpen={orderAndItemIsOpen}
          idScreen="RouteMonitor"
          closeAction={toggleOrderAndItemsModal}
          id="route-monitor-line-item"
          parentSelector={() => {
            let container = document.querySelector(
              '.mainContainer .reactivo-tabs.gmTabs #orderAndItemsModal'
            );

            if (!container) {
              container = document.createElement('div');
              container.id = 'orderAndItemsModal';
              document
                .querySelector('.mainContainer .reactivo-tabs.gmTabs')
                .appendChild(container);
            }
            return container;
          }}
          onAfterClose={() => {
            delete App.Forms.RouteDetailsLineItem;
          }}
        />
      )}
    </Stop>
  );
}

StopListItem.propTypes = {
  stop: PropTypes.object.isRequired,
  routeId: PropTypes.number.isRequired,
  stopDescription: PropTypes.string.isRequired
};

export default StopListItem;
